import { getJackpotData, getMultiplierLabel, toSnakeCase, getPageName, getEventPropsBuilder } from '../../utils';
import { ROUTES } from '@/utils/constants';
import type { AmplitudeEventKeys } from '../../amplitude-properties.types';
import type { RefObject } from 'react';
import { useStore } from '@/store/store';
import { RECENTLY_PLAYED_BANNER_ID, ZONE_SLICE_TYPES, FAVORITES_ZONE_NAME } from '@/app/constants/zone';
import type { MultiplierValue } from '@/store/slices/site-wide-jackpot';

const ZONE_LAYOUT_AMPLITUDE_MAP: Record<string, string> = {
    one_row: '1_row',
    two_row: '2_row',
    vertical_featured_grid: 'featured'
};

const toAmplitudeZoneLayout = (slug: string) => ZONE_LAYOUT_AMPLITUDE_MAP[slug] ?? slug;

export const formatOneBasedPosition = (raw: string | undefined | null): string => {
    if (raw === undefined || raw === null || raw === '') {
        return '';
    }
    const index = Number(raw);
    if (!Number.isFinite(index)) {
        return '';
    }
    return String(index + 1);
};

const isSearchTrackingContext = (moduleSnake: string, zoneNameSnake: string): boolean => {
    return (
        moduleSnake === 'search_results' ||
        moduleSnake === 'search_recommended' ||
        zoneNameSnake.startsWith('search_results')
    );
};

type GameSelectedEvent = {
    module: string;
    gameName: string;
    gameUid: string;
    gameProvider?: string;
    zoneName?: string;
    zoneLayout?: string;
    zonePosition?: string;
    pageName?: string;
    zoneType?: string;
    isSwjGame?: boolean;
    isDailyJackpot?: boolean;
    category?: string;
    isDynamicOrderingEnabled?: string | null;
    gamePosition?: string;
    gameCollectionName?: string;
    gameTileType?: 'static' | 'animated';
    animatedTileCount?: number;
    activeCount?: string | null;
    personalized?: string;
    pinnedCategory?: string;
    videoTitle?: string;
    // The title actually on screen when the event fires (rotating zone titles). When set,
    // it populates the reported 'Zone Name' value; zone-identity branching still uses zoneName.
    displayedZoneTitle?: string;
};

type ResolveModuleParams = {
    moduleSnakeCase: string;
    isRewardMachineExitPage: boolean;
    isTopPicksForYou: boolean;
    isRecentlyPlayed: boolean;
    isForYou: boolean;
    isSearch: boolean;
    isFavorites: boolean;
    isCategoryPage: boolean;
    isExploreTrailerModule: boolean;
};

const resolveModule = ({
    moduleSnakeCase,
    isRewardMachineExitPage,
    isTopPicksForYou,
    isRecentlyPlayed,
    isForYou,
    isSearch,
    isFavorites,
    isCategoryPage,
    isExploreTrailerModule
}: ResolveModuleParams): string => {
    if (isRewardMachineExitPage || isTopPicksForYou) {
        return 'game_zone';
    }

    if (isRecentlyPlayed || isForYou) {
        return 'atf_module';
    }

    if (isSearch) {
        return moduleSnakeCase;
    }

    if (isFavorites) {
        return 'game_list';
    }

    if (isCategoryPage) {
        return isExploreTrailerModule ? 'explore_trailer' : 'game_zone';
    }

    return moduleSnakeCase;
};

type ResolveZoneLayoutParams = {
    zoneLayout?: string;
    isCategoryPage: boolean;
    isExploreTrailerModule: boolean;
};

const resolveZoneLayout = ({ zoneLayout, isCategoryPage, isExploreTrailerModule }: ResolveZoneLayoutParams): string => {
    if (zoneLayout) {
        return toAmplitudeZoneLayout(toSnakeCase(zoneLayout));
    }

    if (isCategoryPage && isExploreTrailerModule) {
        return 'video_trailer';
    }

    return '';
};

type ConditionalPropsContext = {
    isSearch: boolean;
    zoneNameSnakeCase: string;
    searchZoneName: string;
    resolvedZoneLayout: string;
};

const buildConditionalProps = (
    e: GameSelectedEvent,
    { isSearch, zoneNameSnakeCase, searchZoneName, resolvedZoneLayout }: ConditionalPropsContext
): Partial<AmplitudeEventKeys> => {
    const conditional: Partial<AmplitudeEventKeys> = {};

    // Report the currently-displayed rotating title when present, else the static zone name.
    // Identity branching (recently-played / favorites / for-you / search) keeps using zoneNameSnakeCase.
    const displayedZoneNameSnakeCase = toSnakeCase(e.displayedZoneTitle || '');
    conditional['Zone Name'] = isSearch ? searchZoneName : displayedZoneNameSnakeCase || zoneNameSnakeCase;

    if (resolvedZoneLayout) {
        conditional['Zone Layout'] = resolvedZoneLayout;
    }

    if (zoneNameSnakeCase && !isSearch) {
        conditional['Dynamic Ordering Active'] = e.isDynamicOrderingEnabled === 'true' ? 'dynamic' : 'static';
    }

    const zoneTypeValue = toSnakeCase(e.zoneType || '');
    if (zoneTypeValue) {
        conditional['Zone Type'] = zoneTypeValue;
    }

    if (e.activeCount !== undefined && e.activeCount !== null && e.activeCount !== '') {
        conditional['Number Of Actives'] = String(e.activeCount);
    }

    if (e.gameCollectionName) {
        conditional['Game Collection Name'] = toSnakeCase(e.gameCollectionName);
    }

    if (e.gameTileType) {
        conditional['Game Tile Type'] = e.gameTileType;
    }

    if (e.animatedTileCount !== undefined) {
        conditional['Animated Tile Count'] = e.animatedTileCount;
    }

    return conditional;
};

const getJackpotProps = (
    multiplier: MultiplierValue,
    jackpotType: string | undefined,
    isJackpot: boolean | undefined
): Partial<AmplitudeEventKeys> => ({
    ...getJackpotData(useStore?.getState()?.siteWideJackpot?.updatedTiers),
    Multiplier: multiplier,
    OptInStatus: multiplier > 0 ? 'opted_in' : 'opted_out',
    'Jackpot Type': jackpotType,
    'Is Jackpot': isJackpot ? 'true' : 'false'
});

export const getGameSelectedProps = getEventPropsBuilder((e: GameSelectedEvent): Partial<AmplitudeEventKeys> => {
    const zoneNameSnakeCase = toSnakeCase(e.zoneName || '');
    const isRecentlyPlayed = zoneNameSnakeCase === RECENTLY_PLAYED_BANNER_ID;
    const moduleSnakeCase = toSnakeCase(e?.module);
    const isRewardMachineExitPage =
        e?.pageName === 'reward_machine_exit_screen' || e?.pageName === 'reward_machine_exit';
    const isTopPicksForYou = e.zoneType === ZONE_SLICE_TYPES.TOP_PICKS_FOR_YOU;
    const isForYou = zoneNameSnakeCase.includes('for_you') && !isTopPicksForYou;
    const isCategoryPage = e?.pageName === 'category';
    const isExploreTrailerModule = moduleSnakeCase === 'casino_explore_videos_v3';
    const isSearch = isSearchTrackingContext(moduleSnakeCase, zoneNameSnakeCase);
    const isFavorites = isCategoryPage && zoneNameSnakeCase === FAVORITES_ZONE_NAME;
    const searchZoneName = moduleSnakeCase === 'search_recommended' ? 'search_recommended' : 'search_results';

    const resolvedModule = resolveModule({
        moduleSnakeCase,
        isRewardMachineExitPage,
        isTopPicksForYou,
        isRecentlyPlayed,
        isForYou,
        isSearch,
        isFavorites,
        isCategoryPage,
        isExploreTrailerModule
    });

    const resolvedZoneLayout = resolveZoneLayout({
        zoneLayout: e.zoneLayout,
        isCategoryPage,
        isExploreTrailerModule
    });

    const basePageName = isRewardMachineExitPage ? 'reward_machine_exit' : e?.pageName || 'home';
    const pageName = isSearch ? 'search' : basePageName;

    const favoriteGames = useStore?.getState()?.userContext?.favoriteGames ?? [];
    const isFavorite = favoriteGames?.includes(e.gameUid);

    const multiplier = useStore?.getState()?.siteWideJackpot?.multiplier ?? 0;

    const isJackpot = e.isSwjGame;
    const jackpotType = e.isSwjGame ? 'fdc' : undefined;

    const props: Partial<AmplitudeEventKeys> = {
        event: 'Game Selected',
        Module: resolvedModule,
        'Event Category': 'games',
        'Page Name': pageName,
        'Game Name': toSnakeCase(e.gameName),
        'Game ID': e.gameUid,
        Position: formatOneBasedPosition(e.gamePosition),
        'Zone Position': formatOneBasedPosition(e.zonePosition),
        Category: toSnakeCase(e.category || ''),
        'Is Favorite': isFavorite ? 'true' : 'false',
        'Is Jackpot': isJackpot ? 'true' : 'false',
        'Jackpot Type': jackpotType,
        'FDC Jackpot Multiplier': getMultiplierLabel(multiplier),
        Personalized: e.personalized === 'true' ? 'true' : null,
        'Pinned Category': e.pinnedCategory === 'true' ? 'true' : null
    };

    Object.assign(props, buildConditionalProps(e, { isSearch, zoneNameSnakeCase, searchZoneName, resolvedZoneLayout }));

    if (e.videoTitle) {
        props['Video Title'] = toSnakeCase(e.videoTitle);
    }

    if (!e.isSwjGame) {
        return props;
    }

    return {
        ...props,
        ...getJackpotProps(multiplier, jackpotType, isJackpot)
    };
});

/**
 * Default / trending / animated: `data-t-gameuid` on a parent of the launch `<a>`.
 * Simplified row / daily jackpot: the anchor wraps the tile — attrs sit inside `<a>`.
 */
const resolveGameTrackingElement = (launchAnchor: HTMLElement): HTMLElement | null =>
    launchAnchor.closest('[data-t-gameuid]') ?? launchAnchor.querySelector('[data-t-gameuid]');

const findNearestZoneAncestor = (start: HTMLElement, predicate: (el: Element) => boolean): Element | null => {
    let current: Element | null = start;
    while (current) {
        if (predicate(current)) {
            return current;
        }
        current = current.parentElement;
    }
    return null;
};

const getZoneElementForTracking = (start: HTMLElement): Element | null => {
    const hasNonEmptyZoneName = (el: Element) =>
        el.hasAttribute('data-t-zonename') && Boolean(el.getAttribute('data-t-zonename')?.trim());

    return (
        findNearestZoneAncestor(start, hasNonEmptyZoneName) ??
        findNearestZoneAncestor(start, (el) => el.hasAttribute('data-t-zoneuid')) ??
        findNearestZoneAncestor(start, (el) => el.hasAttribute('data-t-zonetype'))
    );
};

export const getTrackingData = (elementRef: RefObject<HTMLElement>) => {
    if (!elementRef.current || typeof window === 'undefined') {
        return null;
    }

    const root = elementRef.current;

    const trackingModule = root.closest('[data-t-module]')?.getAttribute('data-t-module')?.trim() ?? '';
    const trackingZoneModuleAttr = root.closest('[data-t-zonemodule]')?.getAttribute('data-t-zonemodule')?.trim() ?? '';

    const game = resolveGameTrackingElement(root);
    const gameUid = game?.getAttribute('data-t-gameuid') || '';
    const gamePosition = game?.getAttribute('data-t-gameposition') || '';
    const gameName = game?.getAttribute('data-t-gamename') || '';
    const activeCount = game?.getAttribute('data-t-activecount')?.trim() ?? '';
    const gameProvider = game?.getAttribute('data-t-gameprovider') || '';
    const rankOrder = game?.getAttribute('data-t-rankorder') ?? '';
    const internalCode = game?.getAttribute('data-t-internalcode') ?? '';
    const triggerGameName = game?.getAttribute('data-t-triggergame') ?? '';

    const bannerScope = root.closest('[data-t-bannerid]');
    const analyticsBannerId = bannerScope?.getAttribute('data-t-bannerid')?.trim() || '';
    const bannerName = root.closest('[data-t-bannername]')?.getAttribute('data-t-bannername')?.trim() || '';

    const zoneContainerEl =
        root.closest('[data-t-zoneuid]') ?? root.closest('[data-t-zonetype]') ?? root.closest('[data-t-zonename]');

    const zoneEl = getZoneElementForTracking(root);
    const zoneForAttrs = zoneEl ?? zoneContainerEl;

    const zoneName = (zoneForAttrs?.getAttribute('data-t-zonename')?.trim() || '').trim();
    const zoneLayout = zoneForAttrs?.getAttribute('data-t-zonelayout') || '';
    const zoneModule = zoneForAttrs?.getAttribute('data-t-zonemodule')?.trim() || '';
    const zonePosition =
        zoneForAttrs?.getAttribute('data-t-zoneposition') ||
        root.closest('[data-t-zoneposition]')?.getAttribute('data-t-zoneposition') ||
        '';

    const zoneUid =
        zoneContainerEl?.getAttribute('data-t-zoneuid') || zoneForAttrs?.getAttribute('data-t-zoneuid') || '';

    const zoneType = (
        bannerScope?.getAttribute('data-t-zonetype') ||
        zoneContainerEl?.getAttribute('data-t-zonetype') ||
        zoneForAttrs?.getAttribute('data-t-zonetype') ||
        ''
    ).trim();

    const isSwjGame = root.closest('[data-t-isswj]')?.getAttribute('data-t-isswj') === 'true';

    const category = root.closest('[data-t-categoryuid]');

    const isDynamicOrderingEnabled = root.closest('[data-t-dynamicordering]')?.getAttribute('data-t-dynamicordering');

    const personalizedAttr = root.closest('[data-t-personalized]')?.getAttribute('data-t-personalized');
    const pinnedCategoryAttr = root.closest('[data-t-pinnedcategory]')?.getAttribute('data-t-pinnedcategory');
    const videoTitle = root.closest('[data-t-videotitle]')?.getAttribute('data-t-videotitle') || undefined;
    const pageName = root.closest('[data-t-pagename]')?.getAttribute('data-t-pagename')?.trim();

    const resolvedModule = trackingModule || trackingZoneModuleAttr || zoneModule || zoneName || analyticsBannerId;

    const { pathname } = window.location;
    const gameCollectionName = pathname.startsWith(ROUTES.CATEGORY_HUB)
        ? pathname.slice(ROUTES.CATEGORY_HUB.length)
        : undefined;
    const tileTypeAttr = game?.getAttribute('data-t-game-tile-type');
    const gameTileType =
        tileTypeAttr === 'animated' || tileTypeAttr === 'static' ? (tileTypeAttr as 'animated' | 'static') : undefined;
    const animatedTileCount = zoneContainerEl
        ? zoneContainerEl.querySelectorAll('[data-t-game-tile-type="animated"]')?.length
        : undefined;

    return {
        module: resolvedModule,
        zoneName,
        zoneModule,
        zoneLayout,
        zonePosition,
        zoneType,
        zoneUid,
        analyticsBannerId,
        bannerName,
        gameName,
        gameUid,
        gamePosition,
        gameProvider,
        rankOrder,
        internalCode,
        triggerGameName,
        pageName: pageName || getPageName() || 'home',
        isSwjGame,
        category: category?.getAttribute('data-t-categoryuid') || '',
        isDynamicOrderingEnabled,
        gameCollectionName,
        gameTileType,
        animatedTileCount,
        activeCount: activeCount || undefined,
        personalized: personalizedAttr === 'true' ? 'true' : 'false',
        pinnedCategory: pinnedCategoryAttr === 'true' ? 'true' : 'false',
        videoTitle
    };
};
